import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  LinearProgress,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Search as SearchIcon,
  Email as EmailIcon,
  School as SchoolIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import { useAuth } from '../contexts/AuthContext';

interface Student {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  enrolledCourses: number;
  completedCourses: number;
  totalProgress: number;
  lastActivity: string;
  courses: Array<{
    id: string;
    title: string;
    progress: number;
    status: 'active' | 'completed' | 'overdue';
  }>;
}

const Students: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  const { user: authUser } = useAuth();

  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    try {
      // Mock data - replace with actual API call
      setTimeout(() => {
        const mockStudents: Student[] = [
          {
            id: '1',
            firstName: 'Alice',
            lastName: 'Johnson',
            email: 'alice.johnson@email.com',
            enrolledCourses: 3,
            completedCourses: 1,
            totalProgress: 68,
            lastActivity: '2024-06-25',
            courses: [
              { id: '1', title: 'Web Development', progress: 85, status: 'active' },
              { id: '2', title: 'React Fundamentals', progress: 45, status: 'active' },
              { id: '3', title: 'Database Design', progress: 100, status: 'completed' }
            ]
          },
          {
            id: '2',
            firstName: 'Bob',
            lastName: 'Smith',
            email: 'bob.smith@email.com',
            enrolledCourses: 2,
            completedCourses: 0,
            totalProgress: 42,
            lastActivity: '2024-06-24',
            courses: [
              { id: '1', title: 'Web Development', progress: 30, status: 'overdue' },
              { id: '2', title: 'React Fundamentals', progress: 55, status: 'active' }
            ]
          },
          {
            id: '3',
            firstName: 'Carol',
            lastName: 'Davis',
            email: 'carol.davis@email.com',
            enrolledCourses: 4,
            completedCourses: 2,
            totalProgress: 78,
            lastActivity: '2024-06-26',
            courses: [
              { id: '1', title: 'Web Development', progress: 100, status: 'completed' },
              { id: '2', title: 'React Fundamentals', progress: 100, status: 'completed' },
              { id: '3', title: 'Database Design', progress: 60, status: 'active' },
              { id: '4', title: 'Node.js Backend', progress: 25, status: 'active' }
            ]
          }
        ];

        setStudents(mockStudents);
        setLoading(false);
      }, 1000);
    } catch (error: any) {
      setError('Erreur lors du chargement des apprenants');
      setLoading(false);
    }
  };

  const getProgressColor = (progress: number) => {
    if (progress >= 80) return 'success';
    if (progress >= 50) return 'primary';
    if (progress >= 25) return 'warning';
    return 'error';
  };

  const getStatusChip = (status: string) => {
    const colors = {
      active: 'primary',
      completed: 'success',
      overdue: 'error'
    };
    const labels = {
      active: 'Actif',
      completed: 'Terminé',
      overdue: 'En retard',
    };
    return (
      <Chip
        label={labels[status as keyof typeof labels] || status}
        color={colors[status as keyof typeof colors] as any}
        size="small"
        variant="outlined"
      />
    );
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Apprenant',
      width: 200,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight="500">
            {params.row.firstName} {params.row.lastName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {params.row.email}
          </Typography>
        </Box>
      )
    },
    {
      field: 'enrolledCourses',
      headerName: 'Inscrits',
      width: 100,
      align: 'center',
      renderCell: (params) => (
        <Chip label={params.value} color="primary" size="small" />
      )
    },
    {
      field: 'completedCourses',
      headerName: 'Terminés',
      width: 100,
      align: 'center',
      renderCell: (params) => (
        <Chip label={params.value} color="success" size="small" />
      )
    },
    {
      field: 'totalProgress',
      headerName: 'Progression globale',
      width: 200,
      renderCell: (params) => (
        <Box sx={{ width: '100%' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
            <Typography variant="caption">{params.value}%</Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={params.value}
            color={getProgressColor(params.value)}
            sx={{ height: 6, borderRadius: 3 }}
          />
        </Box>
      )
    },
    {
      field: 'lastActivity',
      headerName: 'Dernière activité',
      width: 120,
      renderCell: (params) => (
        <Typography variant="caption" color="text.secondary">
          {new Date(params.value).toLocaleDateString()}
        </Typography>
      )
    },
    {
      field: 'courses',
      headerName: 'Statut des formations',
      width: 300,
      renderCell: (params) => (
        <Box display="flex" flexWrap="wrap" gap={0.5}>
          {params.value.slice(0, 2).map((course: any) => (
            <Tooltip key={course.id} title={`${course.title}: ${course.progress}%`}>
              {getStatusChip(course.status)}
            </Tooltip>
          ))}
          {params.value.length > 2 && (
            <Chip label={`+${params.value.length - 2}`} size="small" variant="outlined" />
          )}
        </Box>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={() => window.open(`mailto:${params.row.email}`)}
        >
          <EmailIcon />
        </IconButton>
      )
    }
  ];

  const filteredStudents = students.filter(student => {
    const matchesSearch = 
      student.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.email.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || 
      student.courses.some(course => course.status === statusFilter);

    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box mb={4}>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Gestion des apprenants
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Suivez la progression des apprenants et gérez les inscriptions
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Box display="flex" gap={2} mb={4} flexWrap="wrap">
        <Card sx={{ minWidth: 200 }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <SchoolIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
            <Typography variant="h4" fontWeight="bold">
              {students.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total apprenants
            </Typography>
          </CardContent>
        </Card>
        
        <Card sx={{ minWidth: 200 }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <TrendingUpIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
            <Typography variant="h4" fontWeight="bold">
              {Math.round(students.reduce((acc, student) => acc + student.totalProgress, 0) / students.length)}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Progression moyenne
            </Typography>
          </CardContent>
        </Card>
        
        <Card sx={{ minWidth: 200 }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <EmailIcon sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
            <Typography variant="h4" fontWeight="bold">
              {students.filter(s => s.courses.some(c => c.status === 'overdue')).length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Apprenants en retard
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Filters */}
      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        <TextField
          placeholder="Rechercher un apprenant..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 250 }}
        />
        
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Statut</InputLabel>
          <Select
            value={statusFilter}
            label="Statut"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="all">Tous les statuts</MenuItem>
            <MenuItem value="active">Actif</MenuItem>
            <MenuItem value="completed">Terminé</MenuItem>
            <MenuItem value="overdue">En retard</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Students Table */}
      <Card>
        <Box sx={{ height: 600, width: '100%' }}>
                     <DataGrid
             rows={filteredStudents}
             columns={columns}
             initialState={{
               pagination: {
                 paginationModel: { page: 0, pageSize: 10 },
               },
             }}
             pageSizeOptions={[5, 10, 25]}
             disableRowSelectionOnClick
             slots={{
               toolbar: GridToolbar,
             }}
             slotProps={{
               toolbar: {
                 showQuickFilter: true,
                 quickFilterProps: { debounceMs: 500 },
               },
             }}
            sx={{
              border: 'none',
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid #f0f0f0',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#fafafa',
                borderBottom: '2px solid #e0e0e0',
              },
            }}
          />
        </Box>
      </Card>
    </Container>
  );
};

export default Students; 